import type {
  ExistingMemberCandidate,
  FamilyAddContext,
  FamilyMemberDraftItem,
  NewPlateMemberPayload,
} from '../types/family-members.types';
import type { FamilyMemberErrors } from '../../components/notification-modal/utils/notification-family-sale.util';
import {
  familySaleErrorMessage,
  hasMemberErrors,
  normalizePlate,
  previewMemberPrice,
  validateFamilyMember,
} from '../../components/notification-modal/utils/notification-family-sale.util';

/** Pure rules for adding members to a started family (CWS-1581). */

/** What the attendant types for a new vehicle before it becomes a draft. */
export interface NewPlateForm {
  firstName: string;
  lastName: string;
  phone: string;
  plate: string;
  state: string;
  packageId: string;
  discountValue: number | null;
}

export type NewPlateResult =
  | { errors: FamilyMemberErrors; item: null }
  | { errors: FamilyMemberErrors; item: FamilyMemberDraftItem };

const ERROR_MESSAGES: Record<string, string> = {
  OTP_REQUIRED: 'Enter the code sent to the primary.',
  OTP_INVALID: 'That code is not right. Check it and try again.',
  OTP_TOO_MANY_ATTEMPTS: 'Too many wrong codes. Ask for a new code.',
  OTP_RESEND_TOO_SOON: 'A code was just sent. Wait a few seconds before asking for another.',
  OTP_RATE_LIMITED: 'Too many codes were sent. Try again later.',
  OTP_SMS_FAILED: 'The code could not be sent to the primary. Try again.',
  OTP_UNAVAILABLE: 'Codes are unavailable right now. Try again in a moment.',
  FAMILY_CARD_DECLINED: "The primary's saved card was declined. Nothing was charged.",
  FAMILY_PRIMARY_NO_CARD: 'The primary has no saved card to charge.',
  FAMILY_MEMBER_SETUP_FAILED: 'The vehicle could not be added. The charge was refunded.',
  FAMILY_MEMBER_REFUND_PENDING:
    'The vehicle could not be added and the refund did not complete. Do not retry: ask a manager to review the charge.',
  FAMILY_MEMBER_RECORD_FAILED:
    'The vehicle was charged but could not be linked to the family. Contact support.',
  FAMILY_MEMBER_OTHER_LOCATION: 'This membership belongs to another location.',
  FAMILY_GROUP_NOT_FOUND: 'This family plan could not be found. Refresh and try again.',
  FAMILY_EXISTING_DISCOUNT:
    'This membership already has a discount, so it cannot join a family plan.',
  FAMILY_NOT_BILLED_BY_WAY: 'Only memberships billed through Way can join a family plan.',
  FAMILY_MEMBERSHIP_NOT_ACTIVE: 'Only an active membership can join a family plan.',
  FAMILY_PACKAGE_NOT_ELIGIBLE: 'This membership package is not family-enabled.',
  CHECK_FAILED: 'This membership could not be checked right now.',
  VEHICLE_PLATE_TAKEN: 'This plate belongs to another customer.',
};

/** The message for a refusal or a failed member; the server's own text is the last resort. */
export function familyMemberErrorMessage(
  code: string | null | undefined,
  fallback: string
): string {
  return (code && ERROR_MESSAGES[code]) || familySaleErrorMessage(code, fallback);
}

/** "Visa ····4242", or null when there is no saved card. */
export function cardLabel(card: FamilyAddContext['card']): string | null {
  if (!card?.last4) {
    return null;
  }
  const brand = (card.brand || 'Card').replace(/^./, letter => letter.toUpperCase());
  return `${brand} ····${card.last4}`;
}

/** Seats left once the drafts are counted (the primary is already in `active_count`). */
export function seatsLeft(context: FamilyAddContext, draftCount: number): number {
  return Math.max(0, context.settings.max_vehicles - context.active_count - draftCount);
}

/** "2 of 4 vehicles" for the counter, counting the primary and the drafts. */
export function vehicleCounter(context: FamilyAddContext, draftCount: number): string {
  return `${context.active_count + draftCount} of ${context.settings.max_vehicles} vehicles`;
}

/** Plates already in the family or lined up, so a plate is not added twice. */
export function takenPlatesOf(primaryPlate: string, drafts: FamilyMemberDraftItem[]): string[] {
  return [
    primaryPlate,
    ...drafts.map(item => (item.payload.kind === 'new_plate' ? item.payload.plate : item.label)),
  ];
}

/**
 * Validates a new vehicle and turns it into a draft. A new vehicle is charged its price after the
 * family discount now, and pays the same each month.
 */
export function buildNewPlateDraft(
  form: NewPlateForm,
  context: FamilyAddContext,
  takenPlates: string[],
  localId: string
): NewPlateResult {
  const errors = validateFamilyMember(form, takenPlates, context.settings.presets);
  const pkg = context.packages.find(item => item.id === form.packageId);
  if (!pkg && !errors.packageId) {
    errors.packageId = 'Pick a package.';
  }
  if (hasMemberErrors(errors) || !pkg || form.discountValue === null) {
    return { errors, item: null };
  }
  const price = previewMemberPrice(
    pkg.list_price,
    context.settings.discount_type,
    form.discountValue
  );
  const payload: NewPlateMemberPayload = {
    kind: 'new_plate',
    plate: normalizePlate(form.plate),
    state: form.state,
    package_id: form.packageId,
    discount: { type: 'preset', value: form.discountValue },
    ...(form.firstName.trim() ? { first_name: form.firstName.trim() } : {}),
    ...(form.lastName.trim() ? { last_name: form.lastName.trim() } : {}),
    ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
  };
  return {
    errors,
    item: {
      localId,
      payload,
      label: `${payload.plate} · ${pkg.name}`,
      chargeNow: price.recurringPrice,
      recurringPrice: price.recurringPrice,
    },
  };
}

/** An existing member joining: nothing is charged now; the discount shows from the next renewal. */
export function buildExistingDraft(
  candidate: ExistingMemberCandidate,
  discountValue: number,
  context: FamilyAddContext,
  localId: string
): FamilyMemberDraftItem {
  const price = previewMemberPrice(candidate.price, context.settings.discount_type, discountValue);
  return {
    localId,
    payload: {
      kind: 'existing',
      subscription_id: candidate.subscription_id,
      discount: { type: 'preset', value: discountValue },
    },
    label: `${candidate.plate} · ${candidate.package_name} (existing member)`,
    chargeNow: 0,
    recurringPrice: price.recurringPrice,
  };
}

/** The approval type a code is sent for: a new vehicle charges the card, so it is the stricter one. */
export function otpActionFor(drafts: FamilyMemberDraftItem[]): 'add_plate' | 'add_member' {
  return drafts.some(item => item.payload.kind === 'new_plate') ? 'add_plate' : 'add_member';
}

/** The total charged to the primary's card now. */
export function totalChargedNow(drafts: FamilyMemberDraftItem[]): number {
  const cents = drafts.reduce((sum, item) => sum + Math.round(item.chargeNow * 100), 0);
  return cents / 100;
}

/** True when a draft needs the primary's saved card, so a missing card blocks it. */
export function needsSavedCard(drafts: FamilyMemberDraftItem[]): boolean {
  return drafts.some(item => item.payload.kind === 'new_plate');
}

/** "2 added" / "1 added, 1 failed" for the done step. */
export function resultSummary(results: { status: 'added' | 'failed' }[]): string {
  const added = results.filter(result => result.status === 'added').length;
  const failed = results.length - added;
  if (!failed) {
    return added === 1 ? '1 member added.' : `${added} members added.`;
  }
  return added ? `${added} added, ${failed} could not be added.` : 'No members were added.';
}

/**
 * Whether the server gave a final answer to a request (a 4xx). No answer at all (offline, a
 * timeout) or a 5xx may have come after the charge, so the request id is kept for the retry.
 */
export function answeredByServer(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}
